/**
 * EntityWorkReportsTab — self-contained "Rapporte" tab for an entity
 * detail page (D2a tenants, D2b–D2e tenancies, units, applications, landlords).
 *
 * The task detail page composes the section, the hook and the two
 * modals itself because it already owned a report modal from C1.1. An
 * entity tab has no such host, so this component bundles them: hook +
 * section + the SHARED WorkReportFormModal and WorkReportCancelDialog.
 * Still exactly one CRUD path for reports in the app — this only wires
 * the existing pieces together.
 *
 * Scope semantics. A report belongs here when the canonical link table
 * says it is linked to this entity (ME2b) — the list and the KPI row
 * apply the identical clause, so they cannot describe different sets.
 *
 * That set includes the links ME2a DERIVED on write: linking a report to
 * a tenancy also links it to that tenancy's tenant, room, unit and
 * property, and linking it to a room also links it to that room's unit
 * and property. So a property shows the work booked inside it, and a
 * tenant shows the work booked on their tenancy — attribution the D2a
 * comment here used to deny, before ME2a/ME2b moved the truth into the
 * link table. Corrected in D4c3 rather than left to mislead.
 *
 * Derivation runs one way only: never property → its rooms, never tenant
 * → their tenancies. And a report appears ONCE per page however many
 * links point at it — the filter is a correlated ``EXISTS``, which
 * answers a boolean per report rather than emitting a row per match.
 *
 * Creating from here prefills ONLY the entity itself. A tenant can hold
 * several tenancies, so guessing one would be wrong even when exactly
 * one is currently active; the searchable tenancy picker is right there
 * if the user wants to attach one.
 */
import React, { useCallback, useEffect, useState } from "react";

import {
  fetchAdminWorkReportById,
} from "../../../api/adminData";
import useEditDetailLoader from "../../../hooks/useEditDetailLoader";
import { useAuth } from "../../../contexts/AuthContext";
import WorkReportCancelDialog from "../calendar/workReports/WorkReportCancelDialog";
import WorkReportFormModal from "../calendar/workReports/WorkReportFormModal";
import EntityWorkReportsSection from "./entityWorkReports/EntityWorkReportsSection";
import { useEntityWorkReports } from "./entityWorkReports/useEntityWorkReports";

/** Per-scope wording and the ONE createDefaults key each scope prefills.
 * Kept here rather than in the section so the shared component stays free
 * of domain copy, and closed so no caller can invent a filter.
 *
 * ``createKey`` is deliberately the entity's own key and nothing else: a
 * new report from a unit does not guess the landlord, one from a tenancy
 * does not guess the tenant. Every other link stays empty and editable in
 * the searchable pickers.
 *
 * ``heading`` is optional and only set where the generic "Rapporte &
 * Aufwand" would be too vague. The five tab-hosted scopes sit under a
 * "Rapporte" tab that already says which entity they belong to; property
 * and room (D4c3) are plain sections on pages without tabs, so they name
 * their subject themselves. Omitting it keeps the section's default —
 * which is why the five existing entries are untouched. */
const SCOPE_TEXT = {
  tenant: {
    headingId: "tenant-reports-heading",
    emptyText: "Für diesen Mieter wurden noch keine Rapporte erfasst.",
    emptyHint: "Angezeigt werden direkt mit diesem Mieter verknüpfte Rapporte.",
    caption: "Rapporte, die direkt mit diesem Mieter verknüpft sind",
    createKey: "tenant_id",
  },
  tenancy: {
    headingId: "tenancy-reports-heading",
    emptyText:
      "Für dieses Mietverhältnis wurden noch keine Rapporte erfasst.",
    emptyHint:
      "Angezeigt werden direkt mit diesem Mietverhältnis verknüpfte Rapporte.",
    caption: "Rapporte, die direkt mit diesem Mietverhältnis verknüpft sind",
    createKey: "tenancy_id",
  },
  unit: {
    headingId: "unit-reports-heading",
    emptyText: "Für diese Unit wurden noch keine Rapporte erfasst.",
    emptyHint: "Angezeigt werden direkt mit dieser Unit verknüpfte Rapporte.",
    caption: "Rapporte, die direkt mit dieser Unit verknüpft sind",
    createKey: "unit_id",
  },
  application: {
    headingId: "application-reports-heading",
    emptyText: "Für diese Bewerbung wurden noch keine Rapporte erfasst.",
    emptyHint:
      "Angezeigt werden direkt mit dieser Bewerbung verknüpfte Rapporte.",
    caption: "Rapporte, die direkt mit dieser Bewerbung verknüpft sind",
    createKey: "application_id",
  },
  landlord: {
    headingId: "landlord-reports-heading",
    emptyText: "Für diesen Vermieter wurden noch keine Rapporte erfasst.",
    emptyHint:
      "Angezeigt werden direkt mit diesem Vermieter verknüpfte Rapporte.",
    caption: "Rapporte, die direkt mit diesem Vermieter verknüpft sind",
    createKey: "landlord_id",
  },
  // D4c3. The hints below say "sowie" / "auch" on purpose: a property
  // and a room are the two scopes that genuinely collect work booked
  // one level below them, and a user counting hours deserves to be told
  // that rather than left to wonder why a room's report is in the
  // building's total. Only what ME2a actually derives is claimed — a
  // property does NOT collect its rooms' tenants, and nothing flows the
  // other way.
  property: {
    headingId: "property-reports-heading",
    heading: "Rapporte der Liegenschaft",
    emptyText: "Für diese Liegenschaft wurden noch keine Rapporte erfasst.",
    emptyHint:
      "Angezeigt werden Rapporte dieser Liegenschaft sowie ihrer Einheiten und Zimmer.",
    caption:
      "Rapporte, die mit dieser Liegenschaft, ihren Einheiten oder Zimmern verknüpft sind",
    createKey: "property_id",
  },
  room: {
    headingId: "room-reports-heading",
    heading: "Rapporte des Zimmers",
    emptyText: "Für dieses Zimmer wurden noch keine Rapporte erfasst.",
    emptyHint:
      "Angezeigt werden Rapporte dieses Zimmers, auch solche über ein Mietverhältnis dieses Zimmers.",
    caption: "Rapporte, die mit diesem Zimmer verknüpft sind",
    createKey: "room_id",
  },
};

export default function EntityWorkReportsTab({
  scope = "tenant",
  entityId,
  /** Several tenancy cards can be open at once, so the heading id must
   * stay unique per instance — otherwise aria-labelledby resolves to the
   * wrong heading. */
  headingIdSuffix = null,
}) {
  const text = SCOPE_TEXT[scope];
  if (!text) {
    throw new Error(`EntityWorkReportsTab: unsupported scope "${scope}"`);
  }

  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id || null;

  const workReports = useEntityWorkReports({ scope, entityId });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleCreate = useCallback(() => {
    setEditingReport(null);
    setModalOpen(true);
  }, []);

  // PICK1a: these rows are list shapes and carry no link ARRAYS — only
  // the legacy singular ids. Editing one straight from the row and
  // saving would write back at most one link per type and delete the
  // rest, so the row supplies an id and the form gets the full
  // WorkReportDetailRead.
  const { requestEdit: handleEdit } = useEditDetailLoader({
    fetchDetail: fetchAdminWorkReportById,
    onLoaded: useCallback((detail) => {
      setEditingReport(detail);
      setModalOpen(true);
    }, []),
    errorMessage: "Der Rapport konnte nicht geladen werden.",
  });

  /** Reload rows AND the server-side total after any mutation. Both are
   * needed: a changed duration moves the total, and a changed entity
   * link removes the row from this view entirely. */
  const handleSaved = useCallback(() => {
    void workReports.refresh();
  }, [workReports]);

  const createDefaults = entityId ? { [text.createKey]: entityId } : undefined;

  const createButton = (
    <button
      type="button"
      onClick={handleCreate}
      className="rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#c5cbe0] dark:hover:bg-[#0d0f14]"
    >
      + Rapport erfassen
    </button>
  );

  return (
    <>
      <EntityWorkReportsSection
        workReports={workReports}
        scope={scope}
        headingId={
          headingIdSuffix
            ? `${text.headingId}-${headingIdSuffix}`
            : text.headingId
        }
        headingText={text.heading}
        emptyText={text.emptyText}
        emptyHint={text.emptyHint}
        captionText={text.caption}
        headerAction={createButton}
        onCreateClick={handleCreate}
        onEditClick={handleEdit}
        onDeleteClick={setPendingDelete}
      />

      <WorkReportFormModal
        open={modalOpen}
        mode={editingReport ? "edit" : "create"}
        report={editingReport}
        currentUserId={currentUserId}
        createDefaults={createDefaults}
        onOpenChange={(next) => {
          setModalOpen(next);
          if (!next) setEditingReport(null);
        }}
        onSaved={handleSaved}
      />

      <WorkReportCancelDialog
        report={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onCancelled={() => {
          setPendingDelete(null);
          handleSaved();
        }}
      />
    </>
  );
}
